import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Link2, ExternalLink, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_COLOR = {
    ORDER_PLACED: "text-cyan-400 border-cyan-500/30 bg-cyan-500/10",
    ORDER_MATCHED: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
    MARGIN_ADDED: "text-amber-400 border-amber-500/30 bg-amber-500/10",
    EARLY_EXIT: "text-orange-400 border-orange-500/30 bg-orange-500/10",
    LIQUIDATION: "text-rose-400 border-rose-500/30 bg-rose-500/10",
    SETTLEMENT: "text-violet-400 border-violet-500/30 bg-violet-500/10",
};

export default function OnChainTape() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        setRefreshing(true);
        try {
            const r = await axios.get(`${API}/public/onchain/tape?limit=100`);
            setEvents(r.data || []);
        } catch {
            // network hiccup — keep previous
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 10000);
        return () => clearInterval(id);
    }, []);

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)]">
            <section className="border-b border-[#1F1F24] px-5 sm:px-6 lg:px-12 py-12 sm:py-16">
                <div className="max-w-[1280px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">
                        Live on Hyperliquid testnet
                    </div>
                    <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tighter leading-[1.05]">
                        Every BUBE trade, on-chain.
                    </h1>
                    <p className="mt-4 max-w-2xl text-sm sm:text-base text-zinc-400 leading-relaxed">
                        Every order, every match, every margin update, every settlement on the
                        Verslan BUBE testnet produces a real Hyperliquid testnet transaction.
                        Watch them stream in below — click any row to verify on the
                        <a
                            href="https://app.hyperliquid-testnet.xyz/explorer"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300 ml-1"
                        >
                            HL explorer ↗
                        </a>.
                    </p>
                </div>
            </section>

            <section className="px-5 sm:px-6 lg:px-12 py-8">
                <div className="max-w-[1280px] mx-auto">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                            Latest {events.length} attestations · auto-refresh 10s
                        </div>
                        <button
                            onClick={load}
                            data-testid="onchain-refresh"
                            disabled={refreshing}
                            className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-2.5 py-1 rounded-sm disabled:opacity-40"
                        >
                            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                            Refresh
                        </button>
                    </div>

                    <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm overflow-hidden">
                        {loading && (
                            <div className="px-5 py-12 text-center text-[11px] font-mono text-zinc-600">loading on-chain feed…</div>
                        )}
                        {!loading && events.length === 0 && (
                            <div className="px-5 py-12 text-center text-[11px] font-mono text-zinc-600">
                                No on-chain events yet — place a swap to see it appear here.
                            </div>
                        )}
                        <div className="divide-y divide-[#1F1F24]">
                            {events.map((ev) => {
                                const color = EVENT_COLOR[ev.event_type] || "text-zinc-400 border-zinc-700 bg-zinc-700/10";
                                return (
                                    <a
                                        key={ev.id}
                                        href={ev.explorer_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`tape-event-${ev.id}`}
                                        className="block px-4 sm:px-5 py-3 hover:bg-[#0F0F12]/60 transition-colors"
                                    >
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className={`shrink-0 text-[9.5px] font-mono uppercase tracking-[0.2em] px-2 py-1 rounded-sm border ${color}`}>
                                                {ev.event_type.replace("_", " ")}
                                            </span>
                                            <span className="font-mono text-[11px] text-zinc-500 shrink-0">
                                                {new Date(ev.attempted_at).toLocaleString([], { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                            </span>
                                            {ev.metadata?.coin && (
                                                <span className="font-mono text-[11px] text-zinc-300 shrink-0">
                                                    {ev.metadata.coin}
                                                </span>
                                            )}
                                            {ev.metadata?.duration && (
                                                <span className="font-mono text-[10px] text-zinc-500 shrink-0">
                                                    {ev.metadata.duration}
                                                </span>
                                            )}
                                            {ev.metadata?.outcome && (
                                                <span className={`font-mono text-[10px] shrink-0 ${ev.metadata.outcome === "BULLISH" ? "text-emerald-400" : ev.metadata.outcome === "BEARISH" ? "text-rose-400" : "text-zinc-400"}`}>
                                                    {ev.metadata.outcome}
                                                </span>
                                            )}
                                            <span className="font-mono text-[10px] text-zinc-600 truncate">
                                                <span className="hidden md:inline">→ </span>{ev.destination}
                                            </span>
                                            <div className="flex-1" />
                                            <span className="inline-flex items-center gap-1 font-mono text-[10px] text-cyan-400/80 shrink-0">
                                                <Link2 className="w-3 h-3" />
                                                <span className="truncate max-w-[140px] sm:max-w-[220px]">{ev.tx_hash}</span>
                                                <ExternalLink className="w-3 h-3" />
                                            </span>
                                        </div>
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-6 text-center text-[10px] font-mono text-zinc-600">
                        Treasury: <span className="text-zinc-400 break-all">0xd3d0a93a…3df7</span> on Hyperliquid testnet.
                        Each attestation transfers a fixed dust amount (0.0000001 USDC) to the trader's wallet as proof of execution.{" "}
                        <Link to="/how-it-works" className="text-cyan-400 hover:text-cyan-300">How BUBE works →</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
